import React, { ReactElement } from "react";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "styled-components";

import Breadcrumb from "components/Breadcrumb";
import EditWrapper from "components/EditWrapper";
import Hero from "components/Hero/Hero";
import Icon from "components/Icon/Icon";
import Image from "components/Image/Image";
import Link, { Obfuscated } from "components/Link";
import PageCard from "components/PageCard";
import Section from "components/Section";
import useIsInEditMode from "hooks/useIsInEditMode";
import { APPEND_DATA_BEGIN } from "store/actionTypes";
import generateSrcSet from "style/generateSrcSet";
import { addEditAttributes } from "utils/episerver";
import ucFirst from "utils/ucFirst";

import {
    Author,
    CardWrapper,
    ContactInfo,
    Introduction,
    LinkSection,
    Heading,
    LoadMore,
    NewsHeading,
    NewsItems,
    Role,
    StyledContainer,
    StyledWrapper,
    ProfileImage,
    ProfileInfo,
} from "./ProfilePage.styled";
import ProfilePageProps, {
    ProfilePageNewsListingProps,
} from "./ProfilePageProps";

const ProfilePage = ({
    theme = "lightgray",
    contentTheme = "black",
    actionTheme = "coral",
    fullName,
    firstName,
    lastName,
    email,
    role,
    businessArea,
    introduction,
    primaryOffice,
    secondaryOffices,
    mainImage,
    profileImage,
    paginatedList,
    editAttributePrefix = "",
    l18n,
    breadcrumb,
    phoneMobile,
    phoneFixed,
}: ProfilePageProps): ReactElement => {
    const pageSize = paginatedList.pageSize || 20;
    const isInEditMode = useIsInEditMode();
    const dispatch = useDispatch();

    if (profileImage && profileImage.url) {
        profileImage.srcSet = generateSrcSet({
            image: profileImage.url,
            focalPoint: profileImage.focalPoint,
            maxWidth: profileImage.uploadedWidth,
            maxHeight: profileImage.uploadedHeight,
            format: "jpg",
            content: [
                {
                    width: 215,
                    height: 215,
                },
            ],
        });
    }

    const handleLoadMore = () => {
        dispatch({
            type: APPEND_DATA_BEGIN,
            payload: {
                path: location.pathname,
                search: undefined,
                page: paginatedList.currentPage + 1,
                pageSize: pageSize,
            },
        });
    };

    const showLoadMore = (
        list: ProfilePageNewsListingProps | undefined,
    ): boolean => {
        if (list?.items === undefined || list.totalItems <= list.items.length) {
            return false;
        }
        const lastPage = Math.ceil(list.totalItems / pageSize);
        return list.currentPage < lastPage;
    };

    const showContactInfo =
        isInEditMode || email || phoneMobile || phoneFixed || primaryOffice;

    return (
        <ThemeProvider theme={{ theme: theme }}>
            <StyledWrapper $hasImage={!!mainImage} id="maincontent">
                {mainImage && (
                    <Hero
                        key={`Hero`}
                        theme={theme}
                        contentTheme={contentTheme}
                        actionTheme={actionTheme}
                        image={mainImage}
                    />
                )}
                {breadcrumb?.items && (
                    <Breadcrumb items={breadcrumb.items} theme={theme} />
                )}
                <Section alignItems="center" theme={theme}>
                    <StyledContainer>
                        <Author>
                            {profileImage && profileImage.url && (
                                <ProfileImage>
                                    <Image
                                        {...profileImage}
                                        caption={undefined}
                                        overlay={undefined}
                                    />
                                </ProfileImage>
                            )}
                            <ProfileInfo $hasImage={!!profileImage?.url}>
                                {!isInEditMode ? (
                                    <Heading>{fullName}</Heading>
                                ) : (
                                    <Heading>
                                        <span
                                            {...addEditAttributes(
                                                `${editAttributePrefix}FirstName`,
                                            )}
                                        >
                                            {firstName || " "}
                                        </span>{" "}
                                        <span
                                            {...addEditAttributes(
                                                `${editAttributePrefix}LastName`,
                                            )}
                                        >
                                            {lastName || " "}
                                        </span>
                                    </Heading>
                                )}
                                <Role>
                                    {role && <span>{role}</span>}
                                    {role && businessArea && " "}
                                    {businessArea && (
                                        <span>{businessArea}</span>
                                    )}
                                </Role>
                            </ProfileInfo>
                        </Author>
                        {showContactInfo && (
                            <ContactInfo>
                                {(isInEditMode || email) && (
                                    <LinkSection>
                                        <Icon icon="envelope28" />
                                        <EditWrapper
                                            editPropertyName={`${editAttributePrefix}Email`}
                                        >
                                            <Obfuscated
                                                email={email}
                                                obfuscateChildren={false}
                                                hoverSpan={true}
                                            >
                                                <span>{email || ""}</span>
                                            </Obfuscated>
                                        </EditWrapper>
                                    </LinkSection>
                                )}
                                {(isInEditMode || phoneMobile) && (
                                    <LinkSection>
                                        <Icon icon="mobile28" />
                                        <EditWrapper
                                            editPropertyName={`${editAttributePrefix}PhoneNumberMobile`}
                                        >
                                            <Obfuscated
                                                tel={phoneMobile?.replace(
                                                    /\s+/g,
                                                    "",
                                                )}
                                            >
                                                {phoneMobile || ""}
                                            </Obfuscated>
                                        </EditWrapper>
                                    </LinkSection>
                                )}
                                {(isInEditMode || phoneFixed) && (
                                    <LinkSection>
                                        <Icon icon="phone28" />
                                        <EditWrapper
                                            editPropertyName={`${editAttributePrefix}PhoneNumberFixed`}
                                        >
                                            <Obfuscated
                                                tel={phoneFixed?.replace(
                                                    /\s+/g,
                                                    "",
                                                )}
                                            >
                                                {phoneFixed || ""}
                                            </Obfuscated>
                                        </EditWrapper>
                                    </LinkSection>
                                )}
                                {primaryOffice &&
                                    primaryOffice.officeName &&
                                    primaryOffice.url && (
                                        <LinkSection>
                                            <Icon icon="office28" />
                                            <Link
                                                url={primaryOffice?.url}
                                                hoverSpan={true}
                                            >
                                                <span>
                                                    {primaryOffice.addressRow1 &&
                                                    primaryOffice.city
                                                        ? [
                                                              primaryOffice.addressRow1,
                                                              ucFirst(
                                                                  primaryOffice.city,
                                                              ),
                                                          ]
                                                              .filter(Boolean)
                                                              .join(", ")
                                                        : primaryOffice.officeName}
                                                </span>
                                            </Link>
                                        </LinkSection>
                                    )}
                                {secondaryOffices &&
                                    secondaryOffices.length > 0 &&
                                    secondaryOffices.map((office, index) => {
                                        if (!office?.url) return null;

                                        return (
                                            <LinkSection
                                                key={`office-${office.contentId}-${index}`}
                                            >
                                                <Icon icon="office28" />
                                                <Link
                                                    url={office?.url}
                                                    hoverSpan={true}
                                                >
                                                    <span>
                                                        {office.addressRow1 &&
                                                        office.city
                                                            ? [
                                                                  office.addressRow1,
                                                                  ucFirst(
                                                                      office.city,
                                                                  ),
                                                              ]
                                                                  .filter(
                                                                      Boolean,
                                                                  )
                                                                  .join(", ")
                                                            : office.officeName}
                                                    </span>
                                                </Link>
                                            </LinkSection>
                                        );
                                    })}
                            </ContactInfo>
                        )}
                        {(isInEditMode || introduction) && (
                            <Introduction
                                {...addEditAttributes(
                                    `${editAttributePrefix}Introduction`,
                                )}
                            >
                                {introduction}
                            </Introduction>
                        )}
                    </StyledContainer>
                </Section>
                {paginatedList?.items !== undefined &&
                    paginatedList.items.length > 0 && (
                        <NewsItems>
                            {l18n.articlesBy && (
                                <NewsHeading>{l18n.articlesBy}</NewsHeading>
                            )}
                            {paginatedList.items?.map((cardData, index) => (
                                <CardWrapper key={`NewsCard${index}`}>
                                    <PageCard
                                        {...cardData}
                                        size="small"
                                        prefetch={1000} // delay on prefetching in ms
                                    />
                                </CardWrapper>
                            ))}
                            {showLoadMore(paginatedList) && (
                                <LoadMore
                                    variant="outline"
                                    actionTheme="black"
                                    onClick={handleLoadMore}
                                >
                                    {l18n?.loadMore || "Load more"}
                                </LoadMore>
                            )}
                        </NewsItems>
                    )}
            </StyledWrapper>
        </ThemeProvider>
    );
};

export default React.memo(ProfilePage);
